import React, { useEffect, useState } from "react";
import { Plus, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "../../lib/api";
import { AdminHeader, Badge, Field, Panel, borderStyle, inputCls } from "../ui";
import MediaPicker from "../MediaPicker";
import EmailPanel from "./EmailPanel";
import PaymentsPanel from "./PaymentsPanel";
import SmartImage from "../../components/SmartImage";
import { PageLoader } from "../../components/States";

export default function SettingsAdmin() {
  const [s, setS] = useState(null);
  const [stripe, setStripe] = useState(null);
  const [busy, setBusy] = useState(false);
  const [picker, setPicker] = useState(false);

  useEffect(() => {
    adminApi.get("/admin/settings").then((r) => setS(r.data.settings)).catch(() => setS({}));
    adminApi.get("/admin/stripe-status").then((r) => setStripe(r.data)).catch(() => {});
  }, []);

  if (!s) return <PageLoader />;

  const contact = s.contact || {};
  const branding = s.branding || {};
  const stats = s.stats || [];
  const homepage = s.homepage || {};

  const setContact = (k, v) => setS((p) => ({ ...p, contact: { ...p.contact, [k]: v } }));
  const setBranding = (k, v) => setS((p) => ({ ...p, branding: { ...p.branding, [k]: v } }));
  const setHomepage = (k, v) => setS((p) => ({ ...p, homepage: { ...p.homepage, [k]: v } }));
  const setSocial = (k, v) =>
    setS((p) => ({ ...p, contact: { ...p.contact, socials: { ...(p.contact.socials || {}), [k]: v } } }));

  const setStat = (i, k, v) =>
    setS((p) => ({ ...p, stats: p.stats.map((st, j) => (j === i ? { ...st, [k]: v } : st)) }));

  async function save() {
    setBusy(true);
    try {
      await adminApi.put("/admin/settings", {
        contact: s.contact, branding: s.branding, stats: s.stats, homepage: s.homepage,
      });
      toast.success("Settings saved");
    } catch (err) {
      toast.error("Save failed", { description: err?.response?.data?.detail });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div data-testid="admin-settings">
      <AdminHeader title="Site settings" subtitle="Branding, contact details and homepage content">
        <button type="button" onClick={save} disabled={busy} className="btn-gold" data-testid="settings-save-button">
          <Save className="h-4 w-4" /> {busy ? "Saving..." : "Save settings"}
        </button>
      </AdminHeader>

      <div className="grid gap-5 lg:grid-cols-2">
        <Panel>
          <h2 className="mb-4 text-sm font-semibold uppercase tracking-wider gold-text">Branding</h2>
          <div className="space-y-3">
            <div className="flex items-center gap-4">
              <div className="w-40"><SmartImage src={branding.logo} alt="Logo" ratio="aspect-[4/1]" fit="contain" /></div>
              <button type="button" onClick={() => setPicker(true)} className="btn-outline-nq px-3 py-1.5 text-xs" data-testid="settings-logo-picker">
                Change logo
              </button>
            </div>
            <Field label="Business name">
              <input value={branding.name || ""} onChange={(e) => setBranding("name", e.target.value)} className={inputCls} style={borderStyle} />
            </Field>
            <Field label="Tagline">
              <input value={branding.tagline || ""} onChange={(e) => setBranding("tagline", e.target.value)} className={inputCls} style={borderStyle} data-testid="settings-tagline-input" />
            </Field>
            <Field label="Short description">
              <textarea rows={3} value={branding.description || ""} onChange={(e) => setBranding("description", e.target.value)} className={inputCls} style={borderStyle} />
            </Field>
          </div>
        </Panel>

        <Panel>
          <h2 className="mb-4 text-sm font-semibold uppercase tracking-wider gold-text">Contact details</h2>
          <div className="space-y-3">
            <Field label="Phone">
              <input value={contact.phone || ""} onChange={(e) => setContact("phone", e.target.value)} className={inputCls} style={borderStyle} data-testid="settings-phone-input" />
            </Field>
            <Field label="Email">
              <input value={contact.email || ""} onChange={(e) => setContact("email", e.target.value)} className={inputCls} style={borderStyle} data-testid="settings-email-input" />
            </Field>
            <Field label="Address">
              <input value={contact.address || ""} onChange={(e) => setContact("address", e.target.value)} className={inputCls} style={borderStyle} />
            </Field>
            {["facebook", "instagram", "youtube"].map((k) => (
              <Field key={k} label={k}>
                <input
                  value={(contact.socials || {})[k] || ""}
                  onChange={(e) => setSocial(k, e.target.value)}
                  placeholder={`https://${k}.com/...`}
                  className={inputCls}
                  style={borderStyle}
                />
              </Field>
            ))}
          </div>
        </Panel>

        <Panel>
          <h2 className="mb-4 text-sm font-semibold uppercase tracking-wider gold-text">Opening hours</h2>
          <div className="space-y-2">
            {(contact.hours || []).map((h, i) => (
              <div key={i} className="flex gap-2">
                <input
                  value={h.days}
                  onChange={(e) =>
                    setContact("hours", contact.hours.map((x, j) => (j === i ? { ...x, days: e.target.value } : x)))
                  }
                  className={inputCls}
                  style={borderStyle}
                />
                <input
                  value={h.time}
                  onChange={(e) =>
                    setContact("hours", contact.hours.map((x, j) => (j === i ? { ...x, time: e.target.value } : x)))
                  }
                  className={inputCls}
                  style={borderStyle}
                />
                <button
                  type="button"
                  onClick={() => setContact("hours", contact.hours.filter((_, j) => j !== i))}
                  className="rounded p-2 hover:bg-white/10"
                >
                  <Trash2 className="h-4 w-4" style={{ color: "#e07a72" }} />
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={() => setContact("hours", [...(contact.hours || []), { days: "", time: "" }])}
              className="btn-outline-nq px-3 py-1.5 text-xs"
            >
              <Plus className="h-3 w-3" /> Add row
            </button>
          </div>
        </Panel>

        <Panel>
          <h2 className="mb-4 text-sm font-semibold uppercase tracking-wider gold-text">Homepage trust stats</h2>
          <div className="space-y-2">
            {stats.map((st, i) => (
              <div key={i} className="flex gap-2">
                <input value={st.value} onChange={(e) => setStat(i, "value", e.target.value)} className={`${inputCls} max-w-[110px]`} style={borderStyle} />
                <input value={st.label} onChange={(e) => setStat(i, "label", e.target.value)} className={inputCls} style={borderStyle} />
              </div>
            ))}
          </div>

          <h2 className="mb-3 mt-6 text-sm font-semibold uppercase tracking-wider gold-text">Homepage video</h2>
          <Field label="YouTube video ID" hint="The part after watch?v=">
            <input value={homepage.youtube_id || ""} onChange={(e) => setHomepage("youtube_id", e.target.value)} className={inputCls} style={borderStyle} />
          </Field>
        </Panel>

        <Panel className="lg:col-span-2">
          <h2 className="mb-3 text-sm font-semibold uppercase tracking-wider gold-text">Payments</h2>
          {stripe ? (
            <div className="flex flex-wrap items-center gap-3 text-sm">
              <span>Stripe:</span>
              {stripe.configured ? (
                <Badge tone={stripe.mode === "live" ? "green" : "gold"}>{stripe.mode} mode active</Badge>
              ) : (
                <Badge tone="red">not configured</Badge>
              )}
              <span className="text-xs text-[color:var(--nq-stone)]">
                {stripe.configured
                  ? "Card payments are live on checkout."
                  : "Add STRIPE_SECRET_KEY on the server to enable card payments. Until then orders are recorded without payment."}
              </span>
            </div>
          ) : (
            <p className="text-sm text-[color:var(--nq-stone)]">Checking...</p>
          )}
        </Panel>

        <PaymentsPanel />

        <EmailPanel />
      </div>

      {picker && (
        <MediaPicker onClose={() => setPicker(false)} onPick={(m) => { setBranding("logo", m.url); setPicker(false); }} />
      )}
    </div>
  );
}
